// Central company identity/media helpers. Keep company branding strictly tenant-scoped:
// a person's photo must never be treated as a company logo and one tenant's media
// must never be used as another tenant's fallback.
export const resolveCompanyId = (input = {}) => {
  const {
    routeId,
    locationCompany,
    companyData,
    userData,
  } = input || {};
  const candidates = [
    routeId,
    // Also accept a direct company record: resolveCompanyId(company).
    input?.id,
    input?.companyId,
    locationCompany?.id,
    locationCompany?.companyId,
    companyData?.id,
    companyData?.companyId,
    userData?.companyId,
    userData?.company?.companyId,
    userData?.company?.id,
  ];
  return candidates.find((value) => typeof value === 'string' && value.trim())?.trim() || null;
};

export const normalizeCompanyRecord = (record, fallbackId = null) => {
  if (!record) return null;
  const id = record.id || record.companyId || fallbackId || null;
  return id ? { ...record, id, companyId: record.companyId || id } : { ...record };
};

const pick = (...values) => values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

// IMPORTANT: only explicit company-logo fields belong here. Do not fall back to
// profileImage/avatar/image because those are person/profile fields in legacy rows.
export const getCompanyLogoUrl = (company = {}) => pick(
  company.logoUrl,
  company.logo,
  company.companyLogoUrl,
  company.companyLogo,
  company.branding?.logoUrl,
  company.branding?.logo
);

export const getCompanyCoverUrl = (company = {}) => pick(
  company.coverImageUrl,
  company.coverImage,
  company.cover_image,
  company.companyCoverUrl,
  company.companyCover,
  company.branding?.coverImageUrl,
  company.branding?.coverImage,
  company.bannerImageUrl,
  company.bannerImage,
  company.banner
);

export const getPersonPhotoUrl = (person = {}) => pick(
  person.photoURL,
  person.profileImageUrl,
  person.profileImage,
  person.profilePictureUrl,
  person.profilePicture,
  person.avatarUrl,
  person.avatar
);

export const getPlatformLogoUrl = (settings = {}) => {
  const value = settings?.value || {};
  const general = value?.general || settings?.general || {};
  const branding = value?.branding || settings?.branding || general?.branding || {};
  return pick(
    settings?.platformLogoUrl,
    settings?.platformLogo,
    settings?.authLogoUrl,
    settings?.loginLogoUrl,
    settings?.appLogoUrl,
    settings?.logoUrl,
    settings?.logo,
    value?.platformLogoUrl,
    value?.platformLogo,
    value?.authLogoUrl,
    value?.loginLogoUrl,
    value?.logoUrl,
    value?.logo,
    general?.platformLogoUrl,
    general?.platformLogo,
    general?.logoUrl,
    general?.logo,
    branding?.platformLogoUrl,
    branding?.logoUrl,
    branding?.logo,
    branding?.companyLogo
  );
};

export const companyMediaCacheKey = (companyId, field = 'logo') => {
  const id = typeof companyId === 'string' && companyId.trim() ? companyId.trim() : 'unknown';
  return `telcofair_company_${field}:${id}`;
};
